import { useMemo } from 'react';
import { useCurrentContext } from './useCurrentContext';
import ApiClientManager from 'core/apiClient';

/**
 * Returns API config headers based on current URL context.
 *
 * Rule: When URL has tenant=X&realmId=Y params, those values MUST be used
 * for x-tenant and x-realm-id headers. Falls back to auth defaults.
 *
 * @example
 * ```tsx
 * const { contextHeaders, buildConfig } = useContextHeaders();
 *
 * // Use with API functions:
 * fetchTeamApiToken({
 *   params,
 *   token,
 *   config: buildConfig(), // Adds context headers
 * });
 * ```
 */
export const useContextHeaders = () => {
  const { tenant, realmId } = useCurrentContext();

  const contextHeaders = useMemo(() => {
    return ApiClientManager.buildUrlHeaders({
      tenantName: tenant,
      realmId,
    });
  }, [tenant, realmId]);

  /**
   * Build API config with context headers.
   * Merges with any additional config provided.
   */
  const buildConfig = useMemo(() => {
    return (additionalConfig?: ApiConfig): ApiConfig => ({
      ...additionalConfig,
      headers: {
        ...contextHeaders,
        ...additionalConfig?.headers,
      },
    });
  }, [contextHeaders]);

  return {
    /** Headers object with x-tenant and x-realm-id */
    contextHeaders,
    /** Build config with context headers merged in */
    buildConfig,
    /** Current tenant from URL or auth default */
    tenant,
    /** Current realmId from URL or auth default */
    realmId,
  };
};
